import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import { ButtonReset } from '@helpers/global';
import { siteWide } from '@helpers/grid';
import { Layers } from '@helpers/layers';
import { Device, from, until } from '@helpers/media';
import styled, { createGlobalStyle } from 'styled-components';
const GlobalStyles = createGlobalStyle `
  :root {
    --headerNavHeight: 60px;
    --quickLinksHeight: 0px;

    @media ${from(Device.TabletLarge)} {
    --headerNavHeight: 78px;
    --quickLinksHeight: 44px;
    }

    --headerHeight: calc(var(--headerNavHeight) + var(--quickLinksHeight));
    --scrollOffset: var(--headerHeight);
  }

  /** 
  Use darker colour for selection within transparent or dark-themed 
  components to ensure text is legible when highlighted
  */
  [data-theme='dark'],
  [data-theme='transparent'] {
    *::selection {
      background: ${brand.greyMid};
    }
    *::-moz-selection {
      background: ${brand.greyMid};
    }
  }

  /**
  Filled titles appear blank when highlighted unless colour is specifically defined
  */
  [data-theme='dark'] {
    [data-fill='true']::selection {
      color: ${brand.yellow};
    }

    [data-fill='true']::-moz-selection {
      color: ${brand.yellow};
    }
  }

  /** Ensure that position position: sticky elements display correctly */
  .display-contents {
    display: contents;
  }
`;
const Wrapper = styled.div `
  padding-top: var(--headerNavHeight);

  &[data-static='true'] {
    @media ${from(Device.Desktop)} {
      padding-top: 0px;
    }
    @media ${from(Device.TabletLarge)} {
      padding-top: 0px;
    }
  }
`;
const Container = styled.header `
  --textColor: ${brand.black};
  --quickLinksBgColor: ${brand.grey};
  --quickLinksHoverColor: ${brand.yellow};
  --mainBgColor: ${brand.white};

  &[data-theme='transparent'] {
    --textColor: ${brand.white};
    --quickLinksBgColor: transparent;
    --quickLinksHoverColor: ${brand.yellow};
    --mainBgColor: transparent;
  }

  &[data-theme='dark'] {
    --textColor: ${brand.white};
    --quickLinksBgColor: ${brand.black};
    --quickLinksHoverColor: ${brand.yellow};
    --mainBgColor: ${brand.black};
  }

  ${Wrapper}[data-scroll-active='true'] &,
  &[data-menu-active='true'] {
    &[data-theme='transparent'] {
      --quickLinksBgColor: ${brand.grey};
      --quickLinksHoverColor: ${brand.black};
      --mainBgColor: ${brand.white};
      @media ${from(Device.TabletLarge)} {
        --textColor: ${brand.black};
      }
    }
  }

  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: ${Layers.Header};

  @media ${until(Device.TabletLarge)} {
    ${Wrapper}[data-scroll-active='true'] & {
      &[data-theme='transparent'] {
        --mainBgColor: ${brand.black};
      }
    }
  }

  @media ${from(Device.TabletLarge)} {
    ${Wrapper}[data-static='true'] & {
      position: relative;
    }
  }
`;
const Inner = styled.div `
  /* ${siteWide}; */
`;
const QuickLinks = styled.div `
  ${transition('background-color')};

  
    display: none;
    justify-content: flex-end;
    align-items: center;
    gap: 50px;
    height: var(--quickLinksHeight);
    text-decoration: none;

    @media ${from(Device.DesktopSmall)} {
      display: flex;
    }
  
`;
const QuickLink = styled.a `
  ${transition('background-color, color, text-decoration-color')};

  display: flex;
  gap: 3px;
  align-items: center;
  text-decoration: underline;
  text-decoration-color: transparent;
  text-decoration-thickness: 3px;
  text-underline-position: under;
  color: var(--textColor);

  svg {
    ${transition('color')};

    width: 18px;
    height: 18px;
  }

  &:focus,
  &:hover {
    text-decoration-color: var(--quickLinksHoverColor);
  }
`;
const Main = styled.div `
  ${transition('background-color')};

  background-color: var(--mainBgColor);

  ${Inner} {
    display: flex;
    align-items: center;
    gap: ${fluid(18, 34)};
    height: var(--headerNavHeight);
    padding-right: 0;
    padding-left: 45px;

    @media ${until(Device.DesktopSmall)} {
      padding-left: 15px;
    }

    nav {
      flex: 1;
    }
  }
`;
const Logo = styled.a `
  ${transition('color')};

  width: ${fluid(90, 117)};
  color: var(--textColor);
`;
const Cta = styled.div `
  display: flex;
  align-items: center;
  margin-left: auto;
  height: 100%;
`;
const MobileNav = styled.button `
  ${ButtonReset};
  background-color: ${brand.grey};
  border-radius: 50%;
  position: relative;
  width: 36px;
  height: 36px;

  color: var(--textColor);

  @media ${from(Device.DesktopSmall)} {
    display: none;
  }
`;
const SearchMenu = styled.div `
  position: fixed;
  top: var(--headerNavHeight);
  left: 0;
  width: 100%;
  height: calc(100dvh - var(--headerNavHeight));
  background-color: ${brand.black};
  z-index: -1;
  opacity: 0;
  overflow: auto;
  &[data-show='true'] {
    z-index: 1000;
    opacity: 1;
  }

  ${transition('opacity')};
`;
const HeaderStyles = {
    GlobalStyles,
    Container,
    Inner,
    QuickLinks,
    QuickLink,
    Main,
    Logo,
    Cta,
    MobileNav,
    Wrapper,
    SearchMenu
};
export default HeaderStyles;
